/**
 * One onboarding's audit log, for HR, admins and compliance (PRD 11.1):
 * every change, who made it and when, in order. Nothing on this screen can
 * change it, and nothing anywhere can: the table refuses every update and
 * delete, and each row is chained to the one before (decisions.md).
 *
 * PII values were never written to the log, so there is nothing to hide
 * here; a changed private field reads "[PII]".
 */

import { HANDOFF_TARGET_MS, type AccessTime, type AuditEntry, type HandoffReport } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import {
  STATE_LABEL,
  accessText,
  dayText,
  durationText,
  plural,
  siteTimeText,
  whenText,
} from "../lib/words.js";
import { Loading, PageHeader, Problem, useLoad } from "./bits.js";

const titleOf =
  (entries: AuditEntry[]) =>
  (key: string): string =>
    entries.find((entry) => entry.taskKey === key)?.taskTitle ?? key;

/**
 * The Stage 1 gate's number for this onboarding: how long each handoff sat
 * before its owner picked it up (PRD 3.2, `handoffLags()`), the median against
 * the four-hour target, and any handoff nobody has picked up yet -- shown,
 * because a median of the finished ones alone would flatter a dropped one.
 */
function Handoffs({ report, titleOf }: { report: HandoffReport; titleOf: (key: string) => string }) {
  const now = Date.now();
  const slowest = [...report.handoffs].sort((a, b) => b.ms - a.ms).slice(0, 3);
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Handoffs</h2>
      {report.medianMs === null ? (
        <p className="muted">
          No handoffs yet: the clock starts when a card opens because the one before it finished.
        </p>
      ) : (
        <p>
          Median{" "}
          <strong className={report.medianMs <= HANDOFF_TARGET_MS ? "" : "warn"}>
            {durationText(report.medianMs)}
          </strong>{" "}
          from a card opening to its owner picking it up, across {plural(report.handoffs.length, "handoff")}.{" "}
          <span className="muted">The target is under {durationText(HANDOFF_TARGET_MS)}.</span>
        </p>
      )}
      {slowest.length > 0 ? (
        <p className="card-meta">
          {slowest.map((handoff) => (
            <span key={handoff.task} className="chip">
              {titleOf(handoff.task)}: {durationText(handoff.ms)}
            </span>
          ))}
        </p>
      ) : null}
      {report.waiting.length > 0 ? (
        <p className="notice-line warn">
          Not picked up yet:{" "}
          {report.waiting
            .map((entry) => `${titleOf(entry.task)} (${durationText(now - entry.openedAt)})`)
            .join("; ")}
        </p>
      ) : null}
    </div>
  );
}

/**
 * An offboarding's access SLA: for each card that revokes access, the time it
 * was asked for and the time it was done, on the site's clock (`accessTimes()`).
 */
function Access({ times, timeZone }: { times: AccessTime[]; timeZone: string }) {
  const now = Date.now();
  return (
    <div className="panel admin-panel">
      <h2 className="admin-heading">Access</h2>
      <ul className="notes" aria-label="Access asked for and done">
        {times.map((time) => {
          const against = accessText(time.requestedAt, time.doneAt, now);
          return (
            <li key={time.task}>
              <strong>{time.title}</strong>: asked for {siteTimeText(time.requestedAt, timeZone)}
              {time.doneAt !== null ? `; done ${siteTimeText(time.doneAt, timeZone)}` : ""}
              {against ? (
                <>
                  {time.doneAt !== null ? ", " : "; "}
                  <span className={against.late ? "warn" : ""}>{against.text}</span>
                </>
              ) : null}
              .
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const stateWord = (value: string | null): string =>
  value && value in STATE_LABEL ? STATE_LABEL[value as keyof typeof STATE_LABEL] : (value ?? "nothing");

const LEVEL = ["no escalation", "reminded", "manager told", "HR's critical queue"];

/** One row of the log in words. Shared with the organisation's whole log (AuditSearch). */
export function describe(entry: AuditEntry, nameOf: (id: string | null) => string): string {
  switch (entry.action) {
    case "start":
      return `Started the work for ${entry.to ?? "somebody"}${entry.note ? ` (${entry.note})` : ""}`;
    case "move":
      return `Moved the start date: ${entry.from ? dayText(entry.from, true) : "none"} → ${entry.to ? dayText(entry.to, true) : "none"}`;
    case "cancel":
      return "Cancelled the whole thing";
    case "convert":
      return "Converted from temp to full-time";
    case "extend":
      return `Extended the contract${entry.to ? ` to ${dayText(entry.to, true)}` : ""}`;
    case "due":
      return `Due ${entry.from ? dayText(entry.from) : "by the minute"} → ${entry.to ? dayText(entry.to) : "by the minute"}`;
    case "state":
      return `${stateWord(entry.from)} → ${stateWord(entry.to)}`;
    case "notify":
      return "Sent the notice";
    case "subtask":
      return `${entry.to === "true" ? "Ticked" : "Unticked"} "${entry.note ?? ""}"`;
    case "field":
      return `Set ${entry.note ?? "a field"}: ${entry.from ?? "blank"} → ${entry.to ?? "blank"}`;
    case "assign":
      return `Handed from ${nameOf(entry.from)} to ${nameOf(entry.to)}`;
    case "signoff":
      return `${entry.to ? "Signed off" : "Withdrew the sign-off"} for ${entry.note ?? "a department"}`;
    case "escalate":
      if (entry.to === "0") return `Escalation cleared: ${LEVEL[Number(entry.from)] ?? entry.from} → none`;
      return `Escalated: ${LEVEL[Number(entry.from)] ?? entry.from} → ${LEVEL[Number(entry.to)] ?? entry.to}`;
    case "poke":
      return "Checked in on it";
    case "comment":
      return "Commented";
    case "mismatch":
      return `${entry.note ?? "A returned item"}: ${entry.to ?? "?"} came back, ${entry.from ?? "?"} was issued`;
    case "note":
      return `${entry.to === "removed" ? "Removed" : entry.to === "edited" ? "Edited" : "Added"} the handover note on "${entry.note ?? ""}"`;
    case "migrate":
      return `Moved to ${entry.to ?? "a newer version"} from ${entry.from ?? "the version before"}`;
    case "template":
      return entry.to === "added"
        ? "Added by a newer version of the template"
        : (entry.note ?? "Changed by a newer version of the template");
    case "alert":
      return entry.note ?? "Sent an alert";
    case "settings":
      return entry.note ?? "Changed the settings";
    case "staffing":
      return `Changed who does the work at ${entry.note ?? "a site"}`;
    case "template-copy":
      return `Made the template ${entry.to ?? ""}${entry.note ? ` (${entry.note})` : ""}`;
    case "template-import":
      return `Imported the template ${entry.to ?? ""} from a file`;
    case "template-save":
      return `Saved ${entry.to ?? "a template"}`;
    case "template-retire":
      return `Retired the template ${entry.to ?? ""}`;
    case "template-delete":
      return `Deleted the template ${entry.to ?? ""}`;
    case "template-restore":
      return `Brought back the template ${entry.to ?? ""}`;
    case "export":
      return `Exported ${entry.to ?? "some"} rows${entry.note ? ` (${entry.note})` : ""}`;
    case "erase":
      return `Erased ${entry.note ?? "a private value"} (${entry.to ?? "erasure request"})`;
    case "erasure":
      return entry.note ?? `Erasure request #${entry.to ?? ""}`;
    case "department":
      return `${entry.note === "Sign-off" ? "Sign-off" : "Owner"} renamed: ${entry.from ?? ""} → ${entry.to ?? ""}`;
    case "department-add":
      return `Added the department ${entry.to ?? ""}`;
    case "department-rename":
      return `Renamed the department ${entry.from ?? ""} to ${entry.to ?? ""}`;
    case "department-archive":
      return `Archived the department ${entry.to ?? ""}`;
    case "department-use":
      return `${entry.to ?? "A department"}: ${entry.note ?? "where it is used changed"}`;
    case "department-restore":
      return `Brought back the department ${entry.to ?? ""}`;
    case "pii_view":
      return `Viewed ${entry.note ?? "a private value"}`;
    default:
      return entry.action;
  }
}

export function AuditLog({ id, kind }: { id: string; kind?: "offboarding" | undefined }) {
  const { api, nameOf } = useOrg();
  const { data, error } = useLoad(() => api.audit(id), [api, id]);

  if (!data && !error) return <Loading title="Audit log" />;

  return (
    <section className="page">
      <PageHeader
        back={{ to: boardOf(id, kind ?? "onboarding"), label: "Back to the board" }}
        title="Audit log"
        meta="Every change, in order. Nobody can edit or delete it, administrators included."
      />
      <Problem>{error}</Problem>
      <div className="panel">
        {data ? (
          data.intact ? (
            <p className="pill s-sent">Every row checks out against the one before it</p>
          ) : (
            <p className="problem" role="alert">
              A row does not match the chain. The log has been changed underneath utuwrk; report this.
            </p>
          )
        ) : null}
      </div>
      {data ? <Handoffs report={data.handoffs} titleOf={titleOf(data.entries)} /> : null}
      {data && data.access.length > 0 ? <Access times={data.access} timeZone={data.timeZone} /> : null}
      {data ? (
        <ol className="panel audit">
          {data.entries.map((entry) => (
            <li key={entry.seq}>
              <span className="audit-what">
                {entry.taskTitle ? <span className="audit-task">{entry.taskTitle}: </span> : null}
                {describe(entry, nameOf)}
              </span>
              {entry.note &&
              entry.action !== "subtask" &&
              entry.action !== "field" &&
              entry.action !== "signoff" &&
              entry.action !== "start" ? (
                <span className="audit-note">{entry.note}</span>
              ) : null}
              <span className="muted audit-who">
                {entry.actorId === "system" ? "utuwrk, by itself" : entry.actorName || nameOf(entry.actorId)},{" "}
                {whenText(entry.at)}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
